"use client";
import React from "react";
import { Box, Container, Typography, Button } from "@mui/material";
import Image from "next/image";
import NextLink from "next/link";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import ArrowDownwardRoundedIcon from "@mui/icons-material/ArrowDownwardRounded";
import { useTranslation } from "react-i18next";

const ATLAS_BLUE = "#1D8FDC";
const OUTPUT_BLUE = "#1D8FDC";
const ARROW_COLOR = "#8FBFE3";
// Baad mein real Atlas avatar image ka URL yahan daal do
const AVATAR_IMAGE = "https://getgeek-website-assets.b-cdn.net/AtlasRightSideImg.webp";

const arrowSx = {
  fontSize: 16,
  color: ARROW_COLOR,
  stroke: ARROW_COLOR,
  strokeWidth: 1.1, // bold effect
};

const InputCard = ({ text, isArabic }) => (
  <Box
    sx={{
      backgroundColor: "#FFFFFF",
      border: "1px solid #E3EDF7",
      borderRadius: "12px",
      px: 2.5,
      py: 1.6,
      textAlign: isArabic ? "right" : "left",
    }}
  >
    <Typography
      sx={{
        fontSize: { xs: "12px", md: "12.5px" },
        fontWeight: 500,
        color: "#14122B",
        fontFamily: "var(--font-outfit)",
      }}
    >
      {text}
    </Typography>
  </Box>
);

const OutputCard = ({ text, isArabic }) => (
  <Box
    sx={{
      backgroundColor: OUTPUT_BLUE,
      borderRadius: "10px",
      px: 2.5,
      py: 1.4,
      textAlign: isArabic ? "right" : "left",
    }}
  >
    <Typography
      sx={{
        fontSize: { xs: "12px", md: "12.5px" },
        fontWeight: 500,
        color: "#FFFFFF",
        fontFamily: "var(--font-outfit)",
      }}
    >
      {text}
    </Typography>
  </Box>
);

const AtlasHowItWorksSection = () => {
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language === "ar";

  const rawIn = t("atlasHowItWorks.inputs", { returnObjects: true });
  const rawOut = t("atlasHowItWorks.outputs", { returnObjects: true });
  const inputs = Array.isArray(rawIn) ? rawIn : [];
  const outputs = Array.isArray(rawOut) ? rawOut : [];

  const HorizontalArrow = () => (
    <Box
      sx={{
        display: { xs: "none", md: "flex" },
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <ArrowForwardRoundedIcon
        sx={{ ...arrowSx, transform: isArabic ? "scaleX(-1)" : "none" }}
      />
    </Box>
  );

  const VerticalArrow = () => (
    <Box sx={{ display: { xs: "flex", md: "none" }, justifyContent: "center" }}>
      <ArrowDownwardRoundedIcon sx={arrowSx} />
    </Box>
  );

  return (
    <Box
      component="section"
      sx={{
        py: { xs: 6, sm: 8, md: 10 },
        backgroundColor: "#FFFFFF",
        direction: isArabic ? "rtl" : "ltr",
      }}
    >
      <Container maxWidth="md">
        {/* Header */}
        <Box sx={{ textAlign: "center", mb: { xs: 4, md: 6 } }}>
          <Typography
            sx={{
              fontSize: { xs: "10px", sm: "11px" },
              fontWeight: 600,
              color: ATLAS_BLUE,
              letterSpacing: "1.5px",
              textTransform: "uppercase",
              fontFamily: "var(--font-outfit)",
              mb: { xs: 1.5, sm: 2 },
            }}
          >
            {t("atlasHowItWorks.label")}
          </Typography>

          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: "24px", sm: "30px", md: "34px", lg: "38px" },
              fontWeight: 700,
              lineHeight: 1.25,
              color: "#14122B",
              fontFamily: "var(--font-plusjakarta)",
              mb: { xs: 2, sm: 2.5 },
            }}
          >
            {t("atlasHowItWorks.heading")}
          </Typography>

          <Typography
            sx={{
              fontSize: { xs: "13px", sm: "14px", md: "14.5px" },
              color: "#55556E",
              lineHeight: 1.8,
              fontWeight: 400,
              fontFamily: "var(--font-outfit)",
              maxWidth: 640,
              mx: "auto",
            }}
          >
            {t("atlasHowItWorks.description")}
          </Typography>
        </Box>

        {/* Flow */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              md: "1fr 20px 1.1fr 20px 0.85fr",
            },
            alignItems: "center",
            columnGap: { xs: 0, md: 2 }, // arrow ke dono taraf barabar chhota gap
            rowGap: 1.5,
            mb: { xs: 4, md: 6 },
          }}
        >
          {/* Inputs */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
            {inputs.map((text, i) => (
              <InputCard key={i} text={text} isArabic={isArabic} />
            ))}
          </Box>

          <HorizontalArrow />
          <VerticalArrow />

          {/* Center Atlas card */}
          <Box
            sx={{
              backgroundColor: "#14122B",
              borderRadius: "14px",
              py: { xs: 4, md: 5.5 },
              px: 3,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 1.25,
            }}
          >
            <Box
              sx={{
                position: "relative",
                width: 48,
                height: 48,
                borderRadius: "50%",
                overflow: "hidden",
                border: "2px solid rgba(255,255,255,0.85)",
              }}
            >
              {AVATAR_IMAGE && (
                <Image
                  src={AVATAR_IMAGE}
                  alt="Atlas AI talent intelligence agent"
                  fill
                  sizes="48px"
                  style={{ objectFit: "cover" }}
                />
              )}
            </Box>

            <Typography
              sx={{
                fontSize: { xs: "15px", md: "16px" },
                fontWeight: 700,
                letterSpacing: "2.5px",
                color: "#FFFFFF",
                fontFamily: "var(--font-plusjakarta)",
                textTransform: "uppercase",
                mt: 0.5,
              }}
            >
              {t("atlasHowItWorks.center.name")}
            </Typography>

            <Typography
              sx={{
                fontSize: { xs: "10px", md: "10.5px" },
                color: "rgba(255,255,255,0.85)",
                fontFamily: "var(--font-outfit)",
                letterSpacing: "0.3px",
              }}
            >
              {t("atlasHowItWorks.center.role")}
            </Typography>
          </Box>

          <HorizontalArrow />
          <VerticalArrow />

          {/* Outputs */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
            {outputs.map((text, i) => (
              <OutputCard key={i} text={text} isArabic={isArabic} />
            ))}
          </Box>
        </Box>

        {/* CTA */}
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <NextLink
            href="/free-trial-of-ai-recruiting-tool"
            passHref
            legacyBehavior
          >
            <Button
              component="a"
              sx={{
                bgcolor: "#000",
                color: "white",
                px: { xs: 3, sm: 3.5 },
                py: { xs: 1.2, sm: 1.4 },
                borderRadius: "50px",
                textTransform: "none",
                fontSize: { xs: "14px", sm: "15px" },
                fontWeight: 500,
                fontFamily: "var(--font-outfit)",
                whiteSpace: "nowrap",
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  bgcolor: "#1D8FDC",
                  transform: "scale(1.03)",
                },
              }}
            >
              {t("atlasHowItWorks.cta")}
            </Button>
          </NextLink>
        </Box>
      </Container>
    </Box>
  );
};

export default AtlasHowItWorksSection;